import { store, store$ } from '@redux/store';
import {
  getTourableSpecsByPlanId$,
  ModalStep,
  selectTourableSpecs,
  selfGuidedActions,
  SelfGuidedTourModal,
} from 'features/selfGuidedTour';
import { filter, fromEvent, map, of, take, withLatestFrom } from 'rxjs';

const configuration = {
  selfGuidedTourInstance: null,
};

/**
 * Updates the initial state content for the self-guided tour modal.
 *
 * - Constructs a specData object from the provided spec, extracting relevant fields.
 * - Finds the DOM element with [data-self-guided-tour-data].
 * - Merges the new specData with any existing JSON data in the element.
 * - Updates the element's innerHTML with the merged JSON string.
 *
 * @param {Object} spec - The specification object containing property details.
 */
const updateInitialStateContent = (spec) => {
  const container = document.querySelector('[data-self-guided-tour-data]');

  const jsonData = container ? JSON.parse(container.innerHTML) : {};

  const specData = {
    brandName: spec.BrandName,
    communityName: jsonData.communityName || spec.CommunityName,
    communityId: spec.CommunityId,
    specId: spec.Id,
    specName: spec.Name,
    planName: spec.PlanName,
    planId: spec.PlanId,
    price: spec.Price,
    bedrooms: spec.Bedrooms,
    bathrooms: spec.Bathrooms,
    garages: spec.Garages,
    squareFeet: spec.SquareFeet,
    address: spec.Address,
    zip: spec.Zip,
    city: spec.City,
    state: spec.StateName,
    imageUrl: spec.Thumbnail,
  };

  container.innerHTML = JSON.stringify({
    ...jsonData,
    ...specData,
  });
};

/**
 * Opens the self-guided tour modal for a plan.
 *
 * - If there is only one tourable spec, opens the modal directly for that spec.
 * - If there are multiple specs, dispatches an action to set the modal step (e.g., to let the user pick a spec).
 *
 * @param {Array} tourableSpecs - The array of tourable specifications for the plan.
 */
const openSelfGuidedTourModal = (tourableSpecs) => {
  if (tourableSpecs.length === 1) {
    updateInitialStateContent(tourableSpecs[0]);
  }

  if (!configuration.selfGuidedTourInstance) {
    configuration.selfGuidedTourInstance = new SelfGuidedTourModal();
  }

  if (tourableSpecs.length === 1) {
    store.dispatch(selfGuidedActions.createModal({ specId: tourableSpecs[0].Id }));
    return;
  }

  if (tourableSpecs.length === 0) {
    store.dispatch(selfGuidedActions.createModal({ specId: null }));
    return;
  }

  store.dispatch(
    selfGuidedActions.createModal({
      specId: null,
      modalStep: ModalStep.SpecSelection,
    })
  );
};

/**
 * Dispatches the provided tourable specs array to the Redux store.
 *
 * @param {Array} tourableSpecs - The array of tourable specifications to store.
 */
const dispatchSelfGuidedTourSpecs = (tourableSpecs) => {
  store.dispatch(selfGuidedActions.setTourableSpecs(tourableSpecs));
};

/**
 * Handles the fetched tourable specs for a plan.
 *
 * - Dispatches the specs to the Redux store.
 * - Enables the self-guided tour button by removing its 'disabled' attribute if it exists in the DOM.
 *
 * @param {Array} tourableSpecs - The array of tourable specifications for the plan.
 */
const handleTourableSpecs = (tourableSpecs) => {
  dispatchSelfGuidedTourSpecs(tourableSpecs);
  const selfGuidedButtons = document.querySelectorAll('[data-self-guided-tour-trigger]');

  selfGuidedButtons.forEach((button) => {
    button.removeAttribute('disabled');
  });
};

/**
 * Reads the community ID from the DOM and fetches tourable specs for that community.
 * Dispatches the fetched specs to the Redux store.
 *
 * - Looks for an element with [data-self-guided-tour-data].
 * - Parses its innerHTML as JSON to extract the communityId.
 * - Calls getTourableSpecsByCommunityId$ with the communityId.
 * - Subscribes to the observable and dispatches the specs to the store.
 */
const getTourableSpecs = () => {
  const selfGuidedTourInformation = document.querySelector('[data-self-guided-tour-data]');

  if (!selfGuidedTourInformation) {
    return;
  }

  const jsonData = selfGuidedTourInformation.innerHTML;
  const { planId } = JSON.parse(jsonData);

  getTourableSpecsByPlanId$(planId).subscribe(handleTourableSpecs);
};

/**
 * Observable that emits when any element with the
 * [data-self-guided-tour-trigger] attribute is clicked.
 */
const onClickSelfGuidedButton$ = fromEvent(document.body, 'click').pipe(
  map((event) => event.target.closest('[data-self-guided-tour-trigger]')),
  filter(Boolean),
  withLatestFrom(store$),
  map(([, storeState]) => selectTourableSpecs(storeState))
);

/**
 * Observable that emits when the DOM is fully loaded.
 * - Emits immediately if the document is already 'complete'.
 * - Otherwise, waits for the 'DOMContentLoaded' event and emits once.
 */
const documentComplete$ =
  document.readyState === 'complete' || document.readyState === 'interactive'
    ? of(null)
    : fromEvent(document, 'DOMContentLoaded').pipe(take(1));

documentComplete$.subscribe(getTourableSpecs);
onClickSelfGuidedButton$.subscribe(openSelfGuidedTourModal);
